import React from 'react';

export default function PayslipDetailModal({
  isOpen,
  onClose,
  records = [],
  summary,
  employeeInfo,
  month,
  year,
}) {
  if (!isOpen) return null;

  const formatCurrency = (val) => {
    const num = Number(val || 0);
    return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const earnings = records.filter((r) => r.drCr === 'Cr');
  const deductions = records.filter((r) => r.drCr === 'Dr');

  const maxRows = Math.max(earnings.length, deductions.length);
  const rows = [];
  for (let i = 0; i < maxRows; i++) {
    rows.push({
      earning: earnings[i] || null,
      deduction: deductions[i] || null,
    });
  }

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4 animate-in fade-in duration-200">
      <div className="flex w-full max-w-4xl max-h-[90vh] flex-col rounded-2xl bg-surface-container-lowest shadow-2xl border border-outline-variant/30 overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-outline-variant/20 px-6 py-4 bg-surface-container-low/40">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <span className="material-symbols-outlined text-lg">receipt_long</span>
            </div>
            <div>
              <h2 className="font-display text-base font-extrabold text-black">
                Salary Pay Slip Statement
              </h2>
              <p className="text-xs text-slate-500 font-medium">
                Period: {month} {year}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handlePrint}
              className="inline-flex items-center gap-1.5 rounded-xl bg-primary/10 px-3.5 py-1.5 text-xs font-bold text-primary hover:bg-primary/20 transition cursor-pointer"
            >
              <span className="material-symbols-outlined text-sm">print</span>
              <span>Print</span>
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-xl hover:bg-surface-container-high transition text-slate-500 hover:text-black cursor-pointer"
            >
              <span className="material-symbols-outlined text-lg">close</span>
            </button>
          </div>
        </div>

        {/* Modal Printable Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 text-sm">
          {/* Company Branding */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-outline-variant/20 pb-4">
            <div>
              <h3 className="font-display text-xl font-black text-primary tracking-tight">
                Bridge Global Enterprise
              </h3>
              <p className="text-xs text-slate-500">Corporate Park, Cyber City, Mumbai - 400051</p>
              <p className="text-xs text-slate-500">CIN: U72200MH2020PTC123456</p>
            </div>
            <div className="mt-2 sm:mt-0 sm:text-right">
              <span className="inline-block px-3 py-1 bg-primary/10 text-primary font-bold text-xs rounded-full uppercase tracking-wider">
                Pay Slip — {month} {year}
              </span>
            </div>
          </div>

          {/* Employee Meta Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 rounded-xl bg-surface-container-low/40 border border-outline-variant/15 text-xs">
            <div>
              <span className="text-slate-500 font-semibold block text-[10px] uppercase tracking-wider">Employee Name</span>
              <span className="font-bold text-black text-sm">{employeeInfo.name}</span>
            </div>
            <div>
              <span className="text-slate-500 font-semibold block text-[10px] uppercase tracking-wider">Employee Code / ID</span>
              <span className="font-bold text-black text-sm">{employeeInfo.code} ({employeeInfo.empId})</span>
            </div>
            <div>
              <span className="text-slate-500 font-semibold block text-[10px] uppercase tracking-wider">Department</span>
              <span className="font-bold text-black text-sm">{employeeInfo.department}</span>
            </div>
            <div>
              <span className="text-slate-500 font-semibold block text-[10px] uppercase tracking-wider">Designation</span>
              <span className="font-bold text-black text-sm">{employeeInfo.designation}</span>
            </div>
            <div>
              <span className="text-slate-500 font-semibold block text-[10px] uppercase tracking-wider">Bank Account</span>
              <span className="font-bold text-black text-sm">{employeeInfo.bankAccount}</span>
            </div>
            <div>
              <span className="text-slate-500 font-semibold block text-[10px] uppercase tracking-wider">Bank Name</span>
              <span className="font-bold text-black text-sm">{employeeInfo.bankName}</span>
            </div>
            <div>
              <span className="text-slate-500 font-semibold block text-[10px] uppercase tracking-wider">PAN</span>
              <span className="font-bold text-black text-sm">{employeeInfo.pan}</span>
            </div>
            <div>
              <span className="text-slate-500 font-semibold block text-[10px] uppercase tracking-wider">PF Number</span>
              <span className="font-bold text-black text-sm">{employeeInfo.pfNo}</span>
            </div>
          </div>

          {/* Earnings & Deductions Dual Table */}
          <div className="overflow-hidden rounded-xl border border-outline-variant/20">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr className="bg-surface-container-low/80 font-bold uppercase tracking-wider text-[10px] border-b border-outline-variant/20">
                  <th className="py-2.5 px-4 text-left text-emerald-800 w-5/12">Earnings (Cr)</th>
                  <th className="py-2.5 px-4 text-right text-emerald-800 w-2/12 border-r border-outline-variant/20">Amount (₹)</th>
                  <th className="py-2.5 px-4 text-left text-rose-800 w-5/12">Deductions (Dr)</th>
                  <th className="py-2.5 px-4 text-right text-rose-800 w-2/12">Amount (₹)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-outline-variant/10">
                {rows.map((row, idx) => (
                  <tr key={idx} className="hover:bg-surface-container-low/30">
                    {/* Earning */}
                    <td className="py-2 px-4 font-semibold text-slate-800">
                      {row.earning?.payHead || ''}
                    </td>
                    <td className="py-2 px-4 text-right font-mono font-bold text-slate-900 border-r border-outline-variant/20">
                      {row.earning ? formatCurrency(row.earning.amount) : ''}
                    </td>
                    {/* Deduction */}
                    <td className="py-2 px-4 font-semibold text-slate-800">
                      {row.deduction?.payHead || ''}
                    </td>
                    <td className="py-2 px-4 text-right font-mono font-bold text-slate-900">
                      {row.deduction ? formatCurrency(row.deduction.amount) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-surface-container-low/80 font-bold border-t border-outline-variant/30 text-xs">
                  <td className="py-3 px-4 text-emerald-800 font-extrabold uppercase">Gross Earnings</td>
                  <td className="py-3 px-4 text-right font-mono font-black text-emerald-700 border-r border-outline-variant/20">
                    {formatCurrency(summary?.grossEarnings)}
                  </td>
                  <td className="py-3 px-4 text-rose-800 font-extrabold uppercase">Total Deductions</td>
                  <td className="py-3 px-4 text-right font-mono font-black text-rose-700">
                    {formatCurrency(summary?.totalDeductions)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Net Salary Highlight Box */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 rounded-xl bg-primary/10 border border-primary/20">
            <div>
              <span className="text-xs font-bold text-primary uppercase tracking-wider block">
                Net Take-Home Salary
              </span>
              <p className="text-xs text-slate-600 font-medium mt-0.5">
                Amount credited to registered bank account
              </p>
            </div>
            <div className="mt-2 sm:mt-0 font-display text-2xl font-black text-primary font-sans tabular-nums">
              {formatCurrency(summary?.netSalary)}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end border-t border-outline-variant/20 px-6 py-3.5 bg-surface-container-low/30">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-surface-variant hover:bg-outline-variant/50 text-on-surface-variant font-bold rounded-xl text-xs transition cursor-pointer"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
